/**
 * The creator's "avoid this direction" preference (plan row 1.8, ADR 0007/0009).
 *
 * Exact-byte rejection is the ENFORCEABLE block: those bytes never come back.
 * This preference is the other half, and it is deliberately weaker — one
 * bounded line per medium that is folded into every Studio generation as
 * steering. It changes what is ASKED FOR; it cannot guarantee that a
 * semantically similar result never recurs, and every surface that shows it
 * must say so. It is never an input to the rejection check.
 *
 * One record on the creation, one writer (`updateCreation`), one reader per
 * medium: the Studio image prompt appends the visual line, and the audio
 * kickoff folds the audio line into the brief as its `avoid` field — which is
 * still creator-authored direction, so ADR 0009 Q1's bound on the brief holds.
 */
import { PlatformError } from '@djgg/core';

export const AVOID_DIRECTION_MEDIA = ['visual', 'audio'] as const;
export type AvoidDirectionMedium = (typeof AVOID_DIRECTION_MEDIA)[number];

/** One line per medium. A key is absent when the creator has no preference. */
export type AvoidDirection = Partial<Record<AvoidDirectionMedium, string>>;

/** Ceiling per line: a preference, not a second prompt. Shared with the audio
 *  brief's `avoid` field so the folded line can never overflow the brief. */
export const MAX_AVOID_DIRECTION_CHARS = 120;

/**
 * Validate a stored or submitted preference: only the two media keys, each a
 * single trimmed line of at most {@link MAX_AVOID_DIRECTION_CHARS}. An empty
 * line clears that medium (the key is dropped), so "no preference" has exactly
 * one representation.
 */
export function validateAvoidDirection(value: unknown): AvoidDirection {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) {
    throw new PlatformError('validation', 'avoidDirection must be an object');
  }
  const record = value as Record<string, unknown>;
  const extra = Object.keys(record).filter((key) => !(AVOID_DIRECTION_MEDIA as readonly string[]).includes(key));
  if (extra.length > 0) {
    throw new PlatformError('validation', `avoidDirection accepts only ${AVOID_DIRECTION_MEDIA.join(', ')} (got ${extra.join(', ')})`);
  }
  const out: AvoidDirection = {};
  for (const medium of AVOID_DIRECTION_MEDIA) {
    const raw = record[medium];
    if (raw === undefined || raw === null) continue;
    if (typeof raw !== 'string') throw new PlatformError('validation', `avoidDirection.${medium} must be text`);
    const trimmed = raw.trim();
    if (!trimmed) continue;
    if (trimmed.length > MAX_AVOID_DIRECTION_CHARS) {
      throw new PlatformError('validation', `avoidDirection.${medium} must be at most ${MAX_AVOID_DIRECTION_CHARS} characters`);
    }
    // eslint-disable-next-line no-control-regex -- the same one-line rule the audio brief enforces
    if (/[\x00-\x1f\x7f]/.test(trimmed)) {
      throw new PlatformError('validation', `avoidDirection.${medium} must be a single line`);
    }
    out[medium] = trimmed;
  }
  return out;
}
